import React, {useEffect, useState} from 'react'
import Grid from "@mui/material/Grid";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from '@mui/icons-material/Add';
import {FormControl, NativeSelect, Select, TextField} from "@mui/material";
import {Controller} from "react-hook-form";
import {DEFAULT_LOCALE, INR_CURRENCY_STYLE} from "../util/constant";
import {MenuItem} from "@material-ui/core";


const UserStrategyRow = ({
                             register,
                             nameKey,
                             index,
                             control,
                             setValue,
                             watch,
                             onAddRow,
                             onDeleteRow,
                             errors,
                             strategyValues,
                             portfolioAmount,
                             setError,
                             clearErrors
                         }) => {
    const shrinkProp = {shrink: true};
    const [maxAmountPerTrade, setMaxAmountPerTrade] = useState(0.0)
    const [slAmountPerTrade, setSlAmountPerTrade] = useState(0.0)
    const [isUpdatingFromPercent, setIsUpdatingFromPercent] = useState(false)
    const [isUpdatingFromAmount, setIsUpdatingFromAmount] = useState(false)
    const [isUpdatingFromQuantity, setIsUpdatingFromQuantity] = useState(false)
    const [isUpdatingFromTslPercentage, setIsUpdatingFromTslPercentage] = useState(false)
    const [isUpdatingFromTslPoints, setIsUpdatingFromTslPoints] = useState(false)
    // const classes = useStyles();

    // Watch for changes in maxPercentPerTrade
    useEffect(() => {
        if (isUpdatingFromPercent) {
            setIsUpdatingFromPercent(false)
            return
        }
        let maxPercentagePerTradeWatch = watch(`${nameKey}.maxPercentPerTrade`);
        const quantityWatch = watch(`${nameKey}.quantity`);
        
        // If quantity has a value, don't update from percentage
        if (quantityWatch && quantityWatch !== 0 && quantityWatch !== '') {
            return
        }
        
        if (maxPercentagePerTradeWatch && maxPercentagePerTradeWatch !== 0 && portfolioAmount > 0) {
            const newAmount = Math.round(parseFloat(maxPercentagePerTradeWatch) * portfolioAmount / 100, 2)
            setMaxAmountPerTrade(newAmount)
            setIsUpdatingFromAmount(true)
            setValue(`${nameKey}.maxAmountPerTrade`, newAmount)
        } else {
            setMaxAmountPerTrade(0.0)
            if (!maxPercentagePerTradeWatch || maxPercentagePerTradeWatch === 0 || maxPercentagePerTradeWatch === '') {
                setIsUpdatingFromAmount(true)
                setValue(`${nameKey}.maxAmountPerTrade`, '')
            }
        }
    }, [watch(`${nameKey}.maxPercentPerTrade`), portfolioAmount])

    // Watch for changes in maxAmountPerTrade
    useEffect(() => {
        if (isUpdatingFromAmount) {
            setIsUpdatingFromAmount(false)
            return
        }
        let maxAmountPerTradeWatch = watch(`${nameKey}.maxAmountPerTrade`);
        const quantityWatch = watch(`${nameKey}.quantity`);
        
        // If quantity has a value, don't update from amount
        if (quantityWatch && quantityWatch !== 0 && quantityWatch !== '') {
            return
        }
        
        // Handle string values (formatted currency) or number values
        let amountValue = 0
        if (typeof maxAmountPerTradeWatch === 'string') {
            // Remove currency formatting (commas, currency symbols)
            amountValue = parseFloat(maxAmountPerTradeWatch.replace(/[₹,\s]/g, '')) || 0
        } else {
            amountValue = parseFloat(maxAmountPerTradeWatch) || 0
        }
        
        if (amountValue > 0 && portfolioAmount > 0) {
            setMaxAmountPerTrade(amountValue)
            
            // Update percentage
            const newPercent = (amountValue / portfolioAmount) * 100
            setIsUpdatingFromPercent(true)
            setValue(`${nameKey}.maxPercentPerTrade`, newPercent.toFixed(2))
        } else {
            setMaxAmountPerTrade(0.0)
            if (!maxAmountPerTradeWatch || maxAmountPerTradeWatch === '' || amountValue === 0) {
                setIsUpdatingFromPercent(true)
                setValue(`${nameKey}.maxPercentPerTrade`, '')
            }
        }
    }, [watch(`${nameKey}.maxAmountPerTrade`), portfolioAmount])

    // Watch for changes in quantity
    useEffect(() => {
        if (isUpdatingFromQuantity) {
            setIsUpdatingFromQuantity(false)
            return
        }
        let quantityWatch = watch(`${nameKey}.quantity`);
        
        if (quantityWatch && quantityWatch !== 0 && quantityWatch !== '') {
            // Quantity has value - clear the other two fields
            setIsUpdatingFromPercent(true)
            setValue(`${nameKey}.maxPercentPerTrade`, '')
            setIsUpdatingFromAmount(true)
            setValue(`${nameKey}.maxAmountPerTrade`, '')
            setMaxAmountPerTrade(0.0)
        }
    }, [watch(`${nameKey}.quantity`)])

    // Watch for changes in maxPercentPerTrade or maxAmountPerTrade to clear quantity
    useEffect(() => {
        const maxPercentWatch = watch(`${nameKey}.maxPercentPerTrade`);
        const maxAmountWatch = watch(`${nameKey}.maxAmountPerTrade`);
        
        // If either percentage or amount has a value, clear quantity
        if ((maxPercentWatch && maxPercentWatch !== 0 && maxPercentWatch !== '') || 
            (maxAmountWatch && maxAmountWatch !== 0 && maxAmountWatch !== '')) {
            const quantityWatch = watch(`${nameKey}.quantity`);
            if (quantityWatch && quantityWatch !== 0 && quantityWatch !== '') {
                setIsUpdatingFromQuantity(true)
                setValue(`${nameKey}.quantity`, '')
            }
        }
    }, [watch(`${nameKey}.maxPercentPerTrade`), watch(`${nameKey}.maxAmountPerTrade`)])

    useEffect(() => {
        let slPercentagePerTradeWatch = watch(`${nameKey}.stopLossInPercentage`);
        if (slPercentagePerTradeWatch && slPercentagePerTradeWatch !== 0) {
            setSlAmountPerTrade(slPercentagePerTradeWatch * maxAmountPerTrade / 100)
        } else {
            setSlAmountPerTrade(0.0)
        }
    }, [watch(`${nameKey}.stopLossInPercentage`), maxAmountPerTrade, portfolioAmount])

    // Watch for changes in tslPercentage
    useEffect(() => {
        if (isUpdatingFromTslPercentage) {
            setIsUpdatingFromTslPercentage(false)
            return
        }
        let tslPercentageWatch = watch(`${nameKey}.tslPercentage`);
        
        if (tslPercentageWatch && tslPercentageWatch !== 0 && tslPercentageWatch !== '') {
            // TSL Percentage has value - clear TSL Points
            const tslPointsWatch = watch(`${nameKey}.tslPoints`);
            if (tslPointsWatch && tslPointsWatch !== 0 && tslPointsWatch !== '') {
                setIsUpdatingFromTslPoints(true)
                setValue(`${nameKey}.tslPoints`, '')
            }
        }
    }, [watch(`${nameKey}.tslPercentage`)])

    // Watch for changes in tslPoints
    useEffect(() => {
        if (isUpdatingFromTslPoints) {
            setIsUpdatingFromTslPoints(false)
            return
        }
        let tslPointsWatch = watch(`${nameKey}.tslPoints`);
        
        if (tslPointsWatch && tslPointsWatch !== 0 && tslPointsWatch !== '') {
            // TSL Points has value - clear TSL Percentage
            const tslPercentageWatch = watch(`${nameKey}.tslPercentage`);
            if (tslPercentageWatch && tslPercentageWatch !== 0 && tslPercentageWatch !== '') {
                setIsUpdatingFromTslPercentage(true)
                setValue(`${nameKey}.tslPercentage`, '')
            }
        }
    }, [watch(`${nameKey}.tslPoints`)])

    const getNested = (theObject, path, separator) => {
        try {
            separator = separator || '.';

            return path.replace('[', separator).replace(']', '').split(separator).reduce(
                function (obj, property) {
                    return obj[property];
                }, theObject
            );

        } catch (err) {
            return undefined;
        }
    }
    const handleChange = (event) => {
        console.log('select change', event.target.value)
        setValue(`${nameKey}.tradeStrategy`, event.target.value);
    };

    let riskPercentage = (slAmountPerTrade * 100 / portfolioAmount).toFixed(2);
    useEffect(() => {
        clearErrors(`${nameKey}.riskPercentage`)
        if (riskPercentage > 2.5) {
            setError(`${nameKey}.riskPercentage`, { type: 'custom', message: 'Risk should be < 2.5%' })
        }

    }, [riskPercentage])
    return (
        <Grid container spacing={2} key={index}>
            <Grid item sx={{display: 'none'}}>
                <TextField hidden={true} {...register(`${nameKey}.rowId`, {required: false})}/>
            </Grid>
            <Grid item xs={8} md={1}>
              <Controller
                name={`${nameKey}.broker`}
                control={control}
                label="Broker"
                labelId='broker-label'
                key={index}
                defaultValue={`${nameKey}.broker`}
                render={({ field }) => <Select {...field}
                                               error={errors && !!getNested(errors, nameKey)?.['broker']}
                                               helperText={errors && getNested(errors, nameKey)?.['broker']?.message}
                                               required
                >
                  <MenuItem key='ANGEL_ONE' value='ANGEL_ONE'>Angel One</MenuItem>
                  <MenuItem key='KOTAK' value='KOTAK'>Kotak</MenuItem>
                  <MenuItem key='DHAN' value='DHAN'>Dhan</MenuItem>
                  <MenuItem key='PROSTOCKS' value='PROSTOCKS'>ProStocks</MenuItem>
                  <MenuItem key='ACA' value='ACA'>ACA</MenuItem>
                </Select>
                }
              />
            </Grid>
            <Grid item xs={4} md={1} sx={{display: {xs: 'block', md: 'none'}}} textAlign="right">
                {<DeleteIcon onClick={onDeleteRow}/>}
                <AddIcon onClick={onAddRow}/>
            </Grid>
            <Grid item xs={10} md={2}>
                <FormControl fullWidth>
                    <Controller
                        name={`${nameKey}.tradeStrategy`}
                        control={control}
                        label="Strategy"
                        key={index}
                        labelId='strategy-label'
                        defaultValue={`${nameKey}.tradeStrategy`}
                        render={({field}) => (
                            <NativeSelect {...field} label="Strategy" onChange={handleChange} autowidth
                                          id='strategy-label' inputProps={{
                                id: 'strategy-label',
                            }}>
                                <option value="0" key="-1">--Select--</option>
                                {strategyValues && strategyValues.length > 0 && strategyValues.map(st =>
                                    <option value={st.name} key={st.name}>{st.displayName}</option>
                                )}
                            </NativeSelect>
                        )}
                    />
                </FormControl>
            </Grid>
            <Grid item xs={4} md={1}>
                <TextField label="Max % / Trade" variant="outlined" size="small" InputLabelProps={shrinkProp}
                           type="float"
                           error={errors && errors['strategies'] && errors['strategies'][index] && !!errors['strategies'][index]['maxPercentPerTrade']}
                           helperText={errors && getNested(errors, nameKey)?.['maxPercentPerTrade']?.message}
                           {...register(`${nameKey}.maxPercentPerTrade`, {
                               required: false,
                               onChange: (e) => {
                                   // Clear quantity when percentage is entered
                                   const quantityValue = watch(`${nameKey}.quantity`);
                                   if (quantityValue && quantityValue !== 0 && quantityValue !== '') {
                                       setValue(`${nameKey}.quantity`, '')
                                   }
                               }
                           })}/>
            </Grid>
            <Grid item xs={6} md={1}>
                <TextField label="or Max Amount / Trade" variant="outlined" size="small" InputLabelProps={shrinkProp}
                           type="number"
                           error={errors && errors['strategies'] && errors['strategies'][index] && !!errors['strategies'][index]['maxAmountPerTrade']}
                           helperText={errors && getNested(errors, nameKey)?.['maxAmountPerTrade']?.message}
                           {...register(`${nameKey}.maxAmountPerTrade`, {
                               required: false,
                               onChange: (e) => {
                                   // Clear quantity when amount is entered
                                   const quantityValue = watch(`${nameKey}.quantity`);
                                   if (quantityValue && quantityValue !== 0 && quantityValue !== '') {
                                       setValue(`${nameKey}.quantity`, '')
                                   }
                               }
                           })}
                />
            </Grid>
            <Grid item xs={6} md={1.5}>
                <TextField label="or Quantity" variant="outlined" size="small" InputLabelProps={shrinkProp}
                           type="number"
                           error={errors && errors['strategies'] && errors['strategies'][index] && !!errors['strategies'][index]['quantity']}
                           helperText={errors && getNested(errors, nameKey)?.['quantity']?.message}
                           {...register(`${nameKey}.quantity`, {
                               required: false,
                               onChange: (e) => {
                                   // Clear percentage and amount when quantity is entered
                                   const percentValue = watch(`${nameKey}.maxPercentPerTrade`);
                                   const amountValue = watch(`${nameKey}.maxAmountPerTrade`);
                                   if ((percentValue && percentValue !== 0 && percentValue !== '') || 
                                       (amountValue && amountValue !== 0 && amountValue !== '')) {
                                       setValue(`${nameKey}.maxPercentPerTrade`, '')
                                       setValue(`${nameKey}.maxAmountPerTrade`, '')
                                       setMaxAmountPerTrade(0.0)
                                   }
                               }
                           })}
                />
            </Grid>
            <Grid item xs={4} md={1}>
                <TextField label="Stop Loss %" variant="outlined" size="small"
                           InputLabelProps={shrinkProp}
                           error={errors && errors['strategies'] && errors['strategies'][index] && !!errors['strategies'][index]['stopLossInPercentage']}
                           helperText={errors && errors['strategies'] && errors['strategies'][index] && getNested(errors, nameKey)?.['stopLossInPercentage']?.message}
                           {...register(`${nameKey}.stopLossInPercentage`, {required: true, min: {value: 0.001, message: 'should be > 0'}, max: {value: 10, message: 'Max 10%'}})}/>
            </Grid>
            <Grid item xs={6} md={1.5}>
                <TextField label="Stop Loss Amount" variant="outlined" size="small"
                           InputLabelProps={shrinkProp}
                           InputProps={{disabled: true}}
                           value={slAmountPerTrade.toLocaleString(DEFAULT_LOCALE, INR_CURRENCY_STYLE)}/>
            </Grid>
            <Grid item xs={6} md={0.5}>
                <TextField label="TSL %" variant="outlined" size="small"
                           InputLabelProps={shrinkProp}
                           type="float"
                           error={errors && errors['strategies'] && errors['strategies'][index] && !!errors['strategies'][index]['tslPercentage']}
                           helperText={errors && errors['strategies'] && errors['strategies'][index] && getNested(errors, nameKey)?.['tslPercentage']?.message}
                           {...register(`${nameKey}.tslPercentage`, {
                               required: false,
                               min: {value: 0.001, message: 'should be > 0'},
                               onChange: (e) => {
                                   // Clear TSL Points when TSL Percentage is entered
                                   const tslPointsValue = watch(`${nameKey}.tslPoints`);
                                   if (tslPointsValue && tslPointsValue !== 0 && tslPointsValue !== '') {
                                       setValue(`${nameKey}.tslPoints`, '')
                                   }
                               }
                           })}
                           />
            </Grid>
            <Grid item xs={6} md={0.75}>
                <TextField label="or TSL Points" variant="outlined" size="small"
                           InputLabelProps={shrinkProp}
                           type="float"
                           error={errors && errors['strategies'] && errors['strategies'][index] && !!errors['strategies'][index]['tslPoints']}
                           helperText={errors && errors['strategies'] && errors['strategies'][index] && getNested(errors, nameKey)?.['tslPoints']?.message}
                           {...register(`${nameKey}.tslPoints`, {
                               required: false,
                               min: {value: 1, message: 'should be > 0'},
                               onChange: (e) => {
                                   // Clear TSL Percentage when TSL Points is entered
                                   const tslPercentageValue = watch(`${nameKey}.tslPercentage`);
                                   if (tslPercentageValue && tslPercentageValue !== 0 && tslPercentageValue !== '') {
                                       setValue(`${nameKey}.tslPercentage`, '')
                                   }
                               }
                           })}
                           />
            </Grid>
            <Grid item xs={5} md={0.75}>
                <TextField label="Portfolio Risk %" variant="outlined" size="small" InputLabelProps={shrinkProp}
                           InputProps={{disabled: true}}
                           error={errors && errors['strategies'] && errors['strategies'][index] && !!errors['strategies'][index]['riskPercentage']}
                           helperText={errors && getNested(errors, nameKey)?.['riskPercentage']?.message}
                           value={riskPercentage}
                           {...register(`${nameKey}.riskPercentage`, {max: {value: 2.5, message: 'Risk should be < 2.5%'}})}
                />
            </Grid>
            <Grid item md={1} textAlign="right" justifyContent='right' alignContent='right'  sx={{display: {xs: 'none', md: 'block', alignItems: 'right', justifyContent: 'right', justifyItems: 'right', justifySelf: 'right',}}}>
                {<DeleteIcon onClick={onDeleteRow}/>}
                <AddIcon onClick={onAddRow}/>
            </Grid>
        </Grid>
    )
}

export default UserStrategyRow